import { clsx } from "clsx";
import { useState, useEffect } from "preact/hooks";
import type { ReactNode } from "preact/compat";
import { mergeRefs } from "react-merge-refs";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import useImageZoomCanvasControls from "@brickme/frontend-common/src/picture-edit/use-image-zoom-canvas-controls.ts";
import useCanvasRenderer from "@brickme/frontend-common/src/picture-render/use-canvas-renderer.ts";
import { useSourceImageBitmap } from "../editor-provider.tsx";
import useImageZoomPinch from "./use-image-zoom-pinch.ts";
import classes from "./picture-render.module.css";

type PictureRenderProps = {
	readonly className?: string;
	readonly overlay?: (size: GenericSize<number>) => ReactNode;
};

function PictureRender({ className, overlay }: PictureRenderProps) {
	// Overlay
	const [overlayChildren, setOverlayChildren] = useState<ReactNode>();

	const sourceImage = useSourceImageBitmap();
	const { canvasRef, canvasParentRef, drawSize } = useCanvasRenderer({
		sourceImage,
		// Only used for debug drawing mode we don't use in here
		faces: undefined,
	});
	useEffect(() => {
		if (overlay) {
			setOverlayChildren(overlay(drawSize));
		} else {
			setOverlayChildren(undefined);
		}
	}, [drawSize, overlay]);

	const { imageZoomCanvasHandlers, isDragging } = useImageZoomCanvasControls({
		sourceImageDimensions: sourceImage,
	});
	const { isPinching, imageZoomPinchCanvasRef } = useImageZoomPinch();

	return (
		<div
			ref={canvasParentRef}
			className={clsx(classes["picture-render"], className)}
		>
			{/* Since we're using offscreen canvas, need to set width height on that */}
			<canvas
				ref={mergeRefs([canvasRef, imageZoomPinchCanvasRef])}
				className={clsx(isDragging && classes["is-dragging"])}
				{...(isPinching ? {} : imageZoomCanvasHandlers)}
				width={0}
				height={0}
				style={{
					touchAction: "none",
					userSelect: "none",
				}}
			/>
			{overlayChildren}
		</div>
	);
}

export default PictureRender;
